import { useState } from 'react';
import Layout from '../components/Layout';
import { useAgents } from '../hooks/useAgents';
import { agents as defaultAgentsList } from '../data/agents';

export default function SettingsPage() {
  const { agents, customPrompts, userAgents, getAgentPrompt, updatePrompt, resetPrompt, addAgent, deleteAgent } = useAgents();

  const [search, setSearch] = useState('');
  const [editingAgent, setEditingAgent] = useState<string | null>(null);
  const [editedPrompt, setEditedPrompt] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [newName, setNewName] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [newPrompt, setNewPrompt] = useState('');
  const [addError, setAddError] = useState('');

  const defaultAgentNames = new Set(defaultAgentsList.map(a => a.name));

  const filteredAgents = agents.filter(
    a => a.name.toLowerCase().includes(search.toLowerCase()) ||
         a.description.toLowerCase().includes(search.toLowerCase())
  );

  const handleEdit = (agentName: string) => {
    const agent = agents.find(a => a.name === agentName);
    if (!agent) return;
    setEditingAgent(agentName);
    setEditedPrompt(getAgentPrompt(agent));
  };

  const handleSave = () => {
    if (!editingAgent) return;
    updatePrompt(editingAgent, editedPrompt);
    setEditingAgent(null);
    setEditedPrompt('');
  };

  const handleReset = () => {
    if (!editingAgent) return;
    resetPrompt(editingAgent);
    const original = defaultAgentsList.find(a => a.name === editingAgent);
    if (original) setEditedPrompt(original.prompt);
  };

  const handleAdd = () => {
    if (!newName.trim()) { setAddError('Name is required'); return; }
    if (!newPrompt.trim()) { setAddError('Prompt is required'); return; }
    if (agents.some(a => a.name === newName.trim())) { setAddError('An agent with this name already exists'); return; }
    addAgent({ name: newName.trim(), description: newDescription.trim(), prompt: newPrompt.trim() });
    setNewName(''); setNewDescription(''); setNewPrompt(''); setShowAddForm(false); setAddError('');
  };

  const handleDelete = (agentName: string) => {
    if (!confirm(`Delete custom agent "${agentName}"?`)) return;
    deleteAgent(agentName);
    if (editingAgent === agentName) { setEditingAgent(null); setEditedPrompt(''); }
  };

  return (
    <Layout>
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <svg className="w-8 h-8 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            <div>
              <h1 className="text-xl font-bold text-gray-800">Settings</h1>
              <p className="text-sm text-gray-500">Manage rewrite agents and their prompts</p>
            </div>
          </div>
          <button onClick={() => setShowAddForm(true)}
            className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white text-sm font-medium rounded-lg hover:bg-purple-700 transition-colors">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
            Add New Agent
          </button>
        </div>

        {/* Search */}
        <div className="relative mb-4">
          <svg className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
          <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search agents..."
            className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent" />
        </div>

        {/* Stats */}
        <div className="flex items-center gap-4 mb-4 text-sm text-gray-500">
          <span>{defaultAgentsList.length} default agents</span>
          <span className="text-gray-300">|</span>
          <span>{userAgents.length} custom agents</span>
          <span className="text-gray-300">|</span>
          <span>{Object.keys(customPrompts).length} modified prompts</span>
        </div>

        {/* Agent List */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm divide-y divide-gray-100">
          {filteredAgents.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <p>No agents found matching "{search}"</p>
            </div>
          ) : (
            filteredAgents.map(agent => {
              const isDefault = defaultAgentNames.has(agent.name);
              const isCustomized = customPrompts[agent.name] !== undefined;
              const isUserAdded = 'isCustom' in agent && agent.isCustom;
              const isEditing = editingAgent === agent.name;

              return (
                <div key={agent.name} className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-medium text-gray-800 truncate">{agent.name}</h3>
                        {isUserAdded && (
                          <span className="px-1.5 py-0.5 text-xs bg-green-100 text-green-700 rounded flex-shrink-0">Custom</span>
                        )}
                        {isCustomized && isDefault && (
                          <span className="px-1.5 py-0.5 text-xs bg-blue-100 text-blue-700 rounded flex-shrink-0">Modified</span>
                        )}
                      </div>
                      <p className="text-sm text-gray-500 truncate">{agent.description}</p>
                    </div>
                    <div className="flex items-center gap-2 ml-4 flex-shrink-0">
                      <button onClick={() => isEditing ? setEditingAgent(null) : handleEdit(agent.name)}
                        className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${isEditing ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
                        {isEditing ? 'Close' : 'Edit Prompt'}
                      </button>
                      {isUserAdded && (
                        <button onClick={() => handleDelete(agent.name)}
                          className="px-3 py-1.5 text-sm bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors">
                          Delete
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Inline editor */}
                  {isEditing && (
                    <div className="mt-4 border-t border-gray-100 pt-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">Agent Prompt</label>
                      <textarea value={editedPrompt} onChange={e => setEditedPrompt(e.target.value)}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none font-mono text-sm"
                        rows={10} />
                      <div className="flex justify-between items-center mt-3">
                        <div>
                          {isDefault && isCustomized && (
                            <button onClick={handleReset} className="text-sm text-gray-500 hover:text-gray-700 transition-colors">
                              Reset to Default
                            </button>
                          )}
                        </div>
                        <div className="flex gap-2">
                          <button onClick={() => { setEditingAgent(null); setEditedPrompt(''); }}
                            className="px-3 py-1.5 text-sm text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors">Cancel</button>
                          <button onClick={handleSave}
                            className="px-3 py-1.5 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors">Save</button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>

        {/* Add Agent Modal */}
        {showAddForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-800">Add New Agent</h3>
              </div>
              <div className="p-6 space-y-4">
                {addError && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">{addError}</div>
                )}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Agent Name</label>
                  <input type="text" value={newName} onChange={e => { setNewName(e.target.value); setAddError(''); }}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    placeholder="e.g., Tone Adjuster" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <input type="text" value={newDescription} onChange={e => setNewDescription(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    placeholder="Brief description of what this agent does" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Prompt</label>
                  <textarea value={newPrompt} onChange={e => { setNewPrompt(e.target.value); setAddError(''); }}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none font-mono text-sm"
                    rows={8} placeholder="Enter the rewrite instructions for this agent..." />
                </div>
              </div>
              <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
                <button onClick={() => { setShowAddForm(false); setNewName(''); setNewDescription(''); setNewPrompt(''); setAddError(''); }}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors">Cancel</button>
                <button onClick={handleAdd}
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors">Add Agent</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
